import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import InvoiceGenerator from "../../applicationUi/components/InvoiceGenerator";
import { fetchInvoicesByAppointmentId } from "../../store/thunks/invoiceThunk";
import { setCurrentInvoice } from "../../store/slices/invoiceSlice";
import { fetchAppointmentById } from "../../store/thunks/appointmentsThunk";
import {
  fetchPayrollById,
  updatePayroll,
  fetchPayrollByInvoiceId,
} from "../../store/thunks/payrollThunk";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import ManualPayrollOverride from "./ManualPayrollOverride";
import payrollDates from "./payrollDates";

function HRPayrollDetails({
  payrollId,
  appointment,
  selectedPeriod,
  selectedPeriodDates,
  submitBy,
  payBy,
  selectedPayrollCohort,
  selectedClinicId,
  onUpdateSuccess,
}) {
  const [fetchedAppointment, setFetchedAppointment] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [overrideInvoice, setOverrideInvoice] = useState(null);
  const [payrollStatusMap, setPayrollStatusMap] = useState({});
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [isWideSidePanelOpen, setIsWideSidePanelOpen] = useState(false);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [payPercentage, setPayPercentage] = useState(0);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  console.log("payrollid", payrollId);
  console.log("RECIEVED APPT IN HRPAYROLLDETAILS", appointment);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Determine the correct appointment ID
        const appointmentId = appointment._id;

        if (!appointmentId) {
          console.error(
            "No valid appointment ID found in appointment data:",
            appointment
          );
          throw new Error("No valid appointment ID found");
        }

        // Fetch the full appointment details
        const fetchedAppointment = await dispatch(
          fetchAppointmentById(appointmentId)
        ).unwrap();
        setFetchedAppointment(fetchedAppointment);

        const payrollResult = await dispatch(
          fetchPayrollById(payrollId)
        ).unwrap();

        console.log("Fetched payroll successfully:", payrollResult);
        setPayrollData(payrollResult);

        const fetchedInvoices = await dispatch(
          fetchInvoicesByAppointmentId(appointmentId)
        ).unwrap();
        setInvoices(fetchedInvoices);

        // Check for payroll existence for each invoice
        const payrollChecks = await Promise.all(
          fetchedInvoices.map(async (invoice) => {
            try {
              const payroll = await dispatch(
                fetchPayrollByInvoiceId(invoice._id)
              ).unwrap();
              return { invoiceId: invoice._id, payroll };
            } catch (error) {
              return { invoiceId: invoice._id, payroll: null }; // If payroll doesn't exist
            }
          })
        );

        // Create a map of payroll statuses by invoiceId
        const statusMap = payrollChecks.reduce(
          (map, { invoiceId, payroll }) => {
            map[invoiceId] = payroll ? payroll._id : null;
            return map;
          },
          {}
        );
        setPayrollStatusMap(statusMap);
        console.log("statusmap", statusMap);

        if (fetchedAppointment?.clinician) {
          setPayPercentage(
            payrollResult?.percentReceived ||
              fetchedAppointment.clinician.defaultPayPercentage ||
              45
          );
        }

        if (fetchedInvoices.length > 0) {
          setSelectedInvoice(fetchedInvoices[0]);
          dispatch(setCurrentInvoice(fetchedInvoices[0]));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [dispatch, appointment, payrollId, isOverrideModalOpen]);

  console.log("fethced payroll", payrollData);

  useEffect(() => {
    if (selectedInvoice) {
      setCalculatedAmount(
        ((selectedInvoice.amount * payPercentage) / 100).toFixed(2)
      );
    }
  }, [selectedInvoice, payPercentage]);

  // jsut for logging purposes
  useEffect(() => {
    if (payrollData) {
      console.log("Updated fetchedPayroll:", payrollData);
    }
  }, [payrollData]);

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    dispatch(setCurrentInvoice(invoice));
    setShowInvoiceGenerator(true);
  };

  const handleOverrideClick = (invoice) => {
    setOverrideInvoice(invoice); // Set the selected invoice for the override
    setIsOverrideModalOpen(true); // Open the modal
  };

  const handleAppointmentClick = async () => {
    try {
      setIsWideSidePanelOpen(true);
      // Use the appointment ID directly
      const appointmentId = appointment._id;

      if (!appointmentId) {
        console.error("No valid appointment ID found:", appointment);
        return;
      }

      const fetchedAppointment = await dispatch(
        fetchAppointmentById(appointmentId)
      ).unwrap();

      console.log("Fetched appointment:", fetchedAppointment);
      setSelectedAppointment(fetchedAppointment);
    } catch (error) {
      console.error("Failed to fetch appointment details:", error);
    }
  };

  const handleCloseWideSidePanel = () => {
    setIsWideSidePanelOpen(false);
  };

  const handleUpdatePayrollStatus = async () => {
    if (!payrollData?.status) {
      setError("Payroll status unavailable. Unable to proceed.");
      return;
    }

    const nextStatus = {
      Carryover: "Ready",
      Pending: "Ready",
      Ready: "Approved",
      Approved: "Paid to Clinician",
    }[payrollData.status];

    if (!nextStatus) {
      setError(
        "Payroll is already marked as 'Paid to Clinician' or an invalid status."
      );
      return;
    }

    const confirmUpdate = window.confirm(
      `Are you sure you want to mark this payroll as '${nextStatus}'?`
    );
    if (!confirmUpdate) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    const updateData = {
      status: nextStatus,
      updatedBy: user._id,
    };

    try {
      await dispatch(updatePayroll({ id: payrollId, updateData })).unwrap();
      setSuccess(`Payroll status updated to '${nextStatus}' successfully!`);
      setPayrollData((prev) => ({
        ...prev,
        status: nextStatus,
      }));

      if (onUpdateSuccess) {
        onUpdateSuccess(); // Notify parent of success
      }
    } catch (err) {
      setError("Failed to update payroll status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const isCompleted =
    appointment?.status.includes("Soap Notes Completed") &&
    selectedInvoice &&
    selectedInvoice.status.toLowerCase() === "paid";

  return (
    <div className="space-y-4">
      <div className="divide-y divide-gray-100">
        <dl className="space-y-4">
          {/* Appointment Details */}
          {appointment && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Appointment Information
                </h3>
              </div>

              <div
                onClick={handleAppointmentClick}
                className="group border border-gray-200 bg-white shadow-sm divide-y divide-gray-100 cursor-pointer hover:border-gray-300 hover:ring-2 hover:ring-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500 transition duration-150 rounded-sm">
                <div className="grid grid-cols-3 gap-2 px-3 py-2">
                  <dt className="text-xs font-medium text-gray-500">
                    Clinician
                  </dt>
                  <dd className="col-span-2 text-xs text-gray-900">
                    {appointment.clinician
                      ? `${appointment.clinician.firstName} ${appointment.clinician.lastName}`
                      : "Clinician info unavailable"}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                  <dt className="text-xs font-medium text-gray-500">Client</dt>
                  <dd className="col-span-2 text-xs text-gray-900">
                    {appointment?.client
                      ? `${appointment.client.firstName} ${appointment.client.lastName}`
                      : "Client info unavailable"}
                  </dd>
                </div>
                <div className="grid grid-cols-3 gap-2 px-3 py-2">
                  <dt className="text-xs font-medium text-gray-500">Service</dt>
                  <dd className="col-span-2 text-xs text-gray-900">
                    {appointment?.service?.description ||
                      "Service info unavailable"}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Payroll Details */}
          {payrollData && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Payroll Information
                </h3>
              </div>

              <div className="border border-gray-200 bg-white shadow-sm mb-3">
                <div className="divide-y divide-gray-100">
                  <div className="grid grid-cols-3 gap-2 px-3 py-2">
                    <dt className="text-xs font-medium text-gray-500">
                      Payroll Amount
                    </dt>
                    <dd className="col-span-2 text-xs text-gray-900 font-semibold">
                      ${payrollData.payrollAmount?.toFixed(2) || "0.00"}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                    <dt className="text-xs font-medium text-gray-500">
                      Pay Percentage
                    </dt>
                    <dd className="col-span-2 text-xs text-gray-900">
                      {payrollData.percentReceived || payPercentage}%
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-3 py-2">
                    <dt className="text-xs font-medium text-gray-500">
                      Status
                    </dt>
                    <dd className="col-span-2 text-xs">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${
                          payrollData.status === "Paid to Clinician"
                            ? "bg-green-100 text-green-800"
                            : payrollData.status === "Approved"
                            ? "bg-blue-100 text-blue-800"
                            : payrollData.status === "Ready"
                            ? "bg-indigo-100 text-indigo-800"
                            : payrollData.status === "Carryover"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-gray-100 text-gray-800"
                        }`}>
                        {payrollData.status}
                      </span>
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                    <dt className="text-xs font-medium text-gray-500">
                      Pay Period
                    </dt>
                    <dd className="col-span-2 text-xs text-gray-900">
                      {payrollData.payPeriodDates || "N/A"}
                    </dd>
                  </div>
                  {payrollData.isCustom && (
                    <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-yellow-50">
                      <dt className="text-xs font-medium text-yellow-700">
                        Custom Entry
                      </dt>
                      <dd className="col-span-2 text-xs text-yellow-900 font-medium">
                        Manual payroll override
                      </dd>
                    </div>
                  )}
                  {payrollData.overrideReason && (
                    <div className="px-3 py-2 bg-amber-50 border-l-4 border-amber-400">
                      <dt className="text-xs font-medium text-amber-800 mb-1 flex items-center">
                        <svg
                          className="h-4 w-4 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Override Reason
                      </dt>
                      <dd className="text-xs text-amber-900 pl-5">
                        {payrollData.overrideReason}
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Invoice Details */}
          {Array.isArray(invoices) && invoices.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Invoice Information
                </h3>
              </div>

              {invoices.map((invoice) => (
                <div
                  key={invoice._id}
                  className="border border-gray-200 bg-white shadow-sm mb-3">
                  <div className="divide-y divide-gray-100">
                    <div className="grid grid-cols-3 gap-2 px-3 py-2">
                      <dt className="text-xs font-medium text-gray-500">
                        Invoice ID
                      </dt>
                      <dd className="col-span-2 text-xs text-gray-900">
                        {invoice.invoiceId || "N/A"}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-gray-50">
                      <dt className="text-xs font-medium text-gray-500">
                        Invoice Amount
                      </dt>
                      <dd className="col-span-2 text-xs text-gray-900">
                        ${invoice.amount?.toFixed(2) || "0.00"}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-2 px-3 py-2">
                      <dt className="text-xs font-medium text-gray-500">
                        Invoice Service
                      </dt>
                      <dd className="col-span-2 text-xs text-gray-900">
                        {invoice.services
                          ?.map((service) => service.description)
                          .join(", ") || "N/A"}
                      </dd>
                    </div>
                    <div className="flex justify-end p-2 bg-gray-50 border-t border-gray-100 items-center">
                      <div className="mr-2 text-xs text-gray-500">
                        {payrollStatusMap[invoice._id] ? (
                          <span>
                            Payroll exists for invoice{" "}
                            <span className="font-medium">
                              {invoice.invoiceId}
                            </span>
                          </span>
                        ) : (
                          <span>No payroll exists</span>
                        )}
                      </div>
                      <button
                        className="text-xs font-medium text-red-600 hover:text-red-500 px-2 py-1 rounded-sm border border-red-200 hover:bg-red-50 transition-colors"
                        onClick={() => handleOverrideClick(invoice)}>
                        Manual Override
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {showInvoiceGenerator && selectedInvoice && (
                <InvoiceGenerator data={selectedInvoice} />
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500 py-2">
              No invoices available.
            </div>
          )}
        </dl>
      </div>

      {/* Action buttons */}
      <div className="mt-4 flex justify-end gap-2">
        {payrollData && (
          <button
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-sm hover:bg-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            onClick={handleUpdatePayrollStatus}
            disabled={isLoading}>
            {isLoading
              ? "Processing..."
              : `Mark As ${
                  payrollData.status === "Pending" || "Carryover"
                    ? "Ready"
                    : payrollData.status === "Ready"
                    ? "Approved"
                    : ""
                }`}
          </button>
        )}
      </div>

      {/* Modals */}
      {isWideSidePanelOpen && selectedAppointment && (
        <AppointmentSummary
          title="Appointment Summary"
          open={isWideSidePanelOpen}
          onClose={handleCloseWideSidePanel}
          appointment={selectedAppointment}
        />
      )}

      <div className="relative z-10">
        <ManualPayrollOverride
          isOpen={isOverrideModalOpen}
          onClose={() => setIsOverrideModalOpen(false)}
          appointment={appointment}
          invoices={overrideInvoice ? [overrideInvoice] : []}
          payrollDates={payrollDates}
          payroll={
            overrideInvoice && payrollStatusMap[overrideInvoice._id]
              ? { _id: payrollStatusMap[overrideInvoice._id] }
              : null
          }
        />
      </div>
    </div>
  );
}

export default HRPayrollDetails;
